import { memo, type MouseEvent } from "react";
import { format } from "date-fns";
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  Globe,
  Award,
  CreditCard,
  Clock,
  Briefcase
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { StatusBadge } from "@/components/ui/status-badge";
import { StarRating } from "@/components/ui/star-rating";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/utilities/tailwind-utils";
import { getInitials } from "@/utilities/typography-utils";
import { ProfileDisplayName } from "@/components/profiles/profile-display-name";
import type { Practice, PracticeWithDetails } from "@/domain/practices";
import type { ProfileCardMode } from "@/types/core";

type PracticeData = Practice | PracticeWithDetails;

export interface PracticeProfileCardProps {
  practice: PracticeData;
  mode?: ProfileCardMode;
  showAccent?: boolean;
  className?: string;
  onClick?: () => void;
  /** Invoked when the practice name is clicked (e.g. navigate to application). Does not bubble to `onClick`. */
  onNameClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  headerAction?: React.ReactNode;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatAddress(practice: PracticeData): string {
  const branch = practice.branches?.[0];
  if (!branch?.address) return "";
  const { street, city, state, postalCode, country } = branch.address;
  return [street, city, state, postalCode, country].filter(Boolean).join(", ");
}

function getPrimaryUser(practice: PracticeData) {
  return "practiceUsers" in practice
    ? practice.practiceUsers?.[0]
    : undefined;
}

// ─── Info Item ───────────────────────────────────────────────────────────────

function InfoItem({
  icon: Icon,
  label,
  value,
  href
}: {
  icon: React.ComponentType<{ className?: string }>;
  label?: string;
  value?: string | null;
  href?: string;
}) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5 min-w-0">
      <div className="flex items-center justify-center size-7 rounded-md bg-muted/60 shrink-0">
        <Icon className="size-3.5 text-muted-foreground" />
      </div>
      <div className="flex flex-col min-w-0 py-0.5">
        {label && (
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-medium leading-none mb-0.5">
            {label}
          </span>
        )}
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary hover:underline truncate"
          >
            {value}
          </a>
        ) : (
          <span className="text-sm truncate">{value}</span>
        )}
      </div>
    </div>
  );
}

// ─── Compact Mode ────────────────────────────────────────────────────────────

const CompactView = memo(function CompactView({
  practice,
  headerAction,
  onNameClick
}: {
  practice: PracticeData;
  headerAction?: React.ReactNode;
  onNameClick?: (event: MouseEvent<HTMLButtonElement>) => void;
}) {
  const primaryUser = getPrimaryUser(practice);
  const initials = getInitials(practice.practiceName || "");
  const address = formatAddress(practice);

  return (
    <div className="flex items-start gap-3">
      <Avatar className="size-11 shrink-0 ring-2 ring-primary/10">
        <AvatarImage src={practice.logoUrl || ""} />
        <AvatarFallback className="bg-primary/8 text-primary font-semibold text-xs">
          {initials}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <ProfileDisplayName compact onClick={onNameClick}>
                {practice.practiceName}
              </ProfileDisplayName>
              <StatusBadge status={practice.verificationStatus} />
            </div>
            {practice.branches?.[0]?.branchName && (
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {practice.branches[0].branchName}
              </p>
            )}
          </div>
          {headerAction}
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-muted-foreground">
          {primaryUser?.user?.email && (
            <span className="flex items-center gap-1 truncate max-w-[180px]">
              <Mail className="size-3 shrink-0" />
              <span className="truncate">{primaryUser.user.email}</span>
            </span>
          )}
          {primaryUser?.user?.phone && (
            <span className="flex items-center gap-1">
              <Phone className="size-3 shrink-0" />
              {primaryUser.user.phone}
            </span>
          )}
          {address && (
            <span className="flex items-center gap-1 truncate max-w-[200px]">
              <MapPin className="size-3 shrink-0" />
              <span className="truncate">{address}</span>
            </span>
          )}
        </div>

        {(practice.averageRating > 0 || practice.totalShiftsCompleted > 0) && (
          <div className="flex items-center gap-3 mt-1.5">
            {practice.averageRating > 0 && (
              <div className="flex items-center gap-1">
                <StarRating
                  value={Math.round(practice.averageRating)}
                  size="sm"
                />
                <span className="text-xs font-medium text-foreground">
                  {practice.averageRating.toFixed(1)}
                </span>
              </div>
            )}
            {practice.totalShiftsCompleted > 0 && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Briefcase className="size-3" />
                {practice.totalShiftsCompleted} shifts
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

// ─── Extended Mode ───────────────────────────────────────────────────────────

const ExtendedView = memo(function ExtendedView({
  practice,
  headerAction,
  onNameClick
}: {
  practice: PracticeData;
  headerAction?: React.ReactNode;
  onNameClick?: (event: MouseEvent<HTMLButtonElement>) => void;
}) {
  const primaryUser = getPrimaryUser(practice);
  const initials = getInitials(practice.practiceName || "");
  const address = formatAddress(practice);
  const websiteHref = practice.website
    ? practice.website.startsWith("http")
      ? practice.website
      : `https://${practice.website}`
    : undefined;

  return (
    <div className="flex flex-col gap-4">
      {/* Profile Header */}
      <div className="flex items-start gap-4">
        <Avatar className="size-14 shrink-0 ring-2 ring-primary/20 shadow-sm">
          <AvatarImage src={practice.logoUrl || ""} />
          <AvatarFallback className="bg-primary/8 text-primary font-bold text-base">
            {initials}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <ProfileDisplayName compact={false} onClick={onNameClick}>
                {practice.practiceName}
              </ProfileDisplayName>
              {practice.branches?.[0]?.branchName && (
                <p className="text-sm text-muted-foreground truncate mt-0.5">
                  {practice.branches[0].branchName}
                </p>
              )}
            </div>
            <StatusBadge status={practice.verificationStatus} />
          </div>

          {(practice.averageRating > 0 ||
            practice.totalShiftsCompleted > 0) && (
              <div className="flex items-center gap-4 mt-2">
                {practice.averageRating > 0 && (
                  <div className="flex items-center gap-1.5">
                    <StarRating
                      value={Math.round(practice.averageRating)}
                      size="sm"
                    />
                    <span className="text-sm font-medium">
                      {practice.averageRating.toFixed(1)}
                    </span>
                  </div>
                )}
                {practice.totalShiftsCompleted > 0 && (
                  <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <Briefcase className="size-3.5" />
                    {practice.totalShiftsCompleted} shifts completed
                  </span>
                )}
              </div>
            )}
          {headerAction && <div className="mt-2">{headerAction}</div>}
        </div>
      </div>

      <Separator />

      {/* Contact & Details Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
        {primaryUser?.user && (
          <InfoItem
            icon={Building2}
            label="Primary Contact"
            value={
              `${primaryUser.user.firstName ?? ""} ${primaryUser.user.lastName ?? ""}`.trim() ||
              undefined
            }
          />
        )}
        <InfoItem icon={Mail} label="Email" value={primaryUser?.user?.email} />
        <InfoItem
          icon={Phone}
          label="Phone"
          value={primaryUser?.user?.phone}
        />
        <InfoItem
          icon={MapPin}
          label="Address"
          value={address || undefined}
        />
        <InfoItem
          icon={Globe}
          label="Website"
          value={practice.website}
          href={websiteHref}
        />
        <InfoItem
          icon={Award}
          label="License Number"
          value={practice.licenseNumber}
        />
        <InfoItem icon={CreditCard} label="Tax ID" value={practice.taxId} />
        {practice.createdAt && (
          <InfoItem
            icon={Clock}
            label="Member Since"
            value={format(new Date(practice.createdAt), "MMM d, yyyy")}
          />
        )}
      </div>

      {/* Description */}
      {practice.description && (
        <>
          <Separator />
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
              About
            </span>
            <div className="rounded-lg bg-muted/40 p-3 border border-border/50">
              <p className="text-sm text-foreground/80 leading-relaxed">
                {practice.description}
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
});

// ─── Main Component ──────────────────────────────────────────────────────────

export const PracticeProfileCard = memo(function PracticeProfileCard({
  practice,
  mode = "compact",
  showAccent = true,
  className,
  onClick,
  onNameClick,
  headerAction
}: PracticeProfileCardProps) {
  return (
    <Card
      className={cn(
        "overflow-hidden transition-all duration-200 py-4",
        mode === "extended" ? "pt-0" : "px-0 pt-0 pb-0",
        onClick &&
        "cursor-pointer hover:shadow-md hover:border-primary/20 active:scale-[0.995]",
        className
      )}
      onClick={onClick}
    >
      {showAccent && mode === "extended" && (
        <div className="h-1 bg-gradient-to-r from-primary/50 via-primary to-primary/50" />
      )}
      <CardContent className={cn(mode === "extended" ? "pt-0 pb-0 px-4" : "py-3 px-3")}>
        {mode === "compact" ? (
          <CompactView
            practice={practice}
            headerAction={headerAction}
            onNameClick={onNameClick}
          />
        ) : (
          <ExtendedView
            practice={practice}
            headerAction={headerAction}
            onNameClick={onNameClick}
          />
        )}
      </CardContent>
    </Card>
  );
});

export default PracticeProfileCard;
